import { Box, Container, Typography, Button, Grid, Card, CardContent, Chip, Stack, Divider, AppBar, Toolbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Key as KeyIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Support as SupportIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Login as LoginIcon,
} from '@mui/icons-material';

export const LandingPage = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <KeyIcon sx={{ fontSize: 48 }} />,
      title: 'ライセンスキー管理',
      description: 'XXXX-XXXX-XXXX-XXXX形式の人間が入力しやすいライセンスキーを自動生成。デバイス認証も完備。',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48 }} />,
      title: 'セキュリティ',
      description: 'HMAC-SHA512署名検証、bcryptハッシュ化、レート制限、監査ログ。エンタープライズ級のセキュリティ。',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      title: '3行で統合',
      description: 'REST APIまたはSDKで、わずか3行のコードでライセンス検証を実装。開発時間を大幅短縮。',
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 48 }} />,
      title: 'OEM/代理店対応',
      description: 'ホワイトラベル、カスタムドメイン、収益分配システム内蔵。代理店ビジネスをすぐに開始。',
    },
    {
      icon: <CodeIcon sx={{ fontSize: 48 }} />,
      title: 'Webhook連携',
      description: 'Stripe、Connected One、PayPal等の決済サービスと自動連携。購入即ライセンス発行。',
    },
    {
      icon: <SupportIcon sx={{ fontSize: 48 }} />,
      title: '日本語サポート',
      description: '日本語UI、日本円決済、国内決済対応。日本市場に特化したライセンス管理SaaS。',
    },
  ];

  const plans = [
    {
      name: 'Free',
      price: '¥0',
      period: '',
      licenses: '100',
      products: '1',
      apiCalls: '1万/月',
      oem: false,
      features: ['ライセンスキー生成', 'デバイス認証', '基本API'],
      recommended: false,
    },
    {
      name: 'Starter',
      price: '¥3,980',
      period: '/月',
      annualPrice: '¥39,800/年',
      licenses: '500',
      products: '3',
      apiCalls: '10万/月',
      oem: false,
      features: ['全機能', 'Webhook連携', 'メールサポート'],
      recommended: false,
    },
    {
      name: 'Pro',
      price: '¥9,800',
      period: '/月',
      annualPrice: '¥98,000/年',
      licenses: '5,000',
      products: '10',
      apiCalls: '100万/月',
      oem: false,
      features: ['全機能', '優先サポート', 'カスタム設定'],
      recommended: true,
    },
    {
      name: 'Business',
      price: '¥29,800',
      period: '/月',
      annualPrice: '¥298,000/年',
      licenses: '50,000',
      products: '無制限',
      apiCalls: '500万/月',
      oem: true,
      features: ['OEM基本機能', 'ホワイトラベル', '代理店3社まで'],
      recommended: false,
    },
    {
      name: 'Enterprise',
      price: '¥98,000',
      period: '/月〜',
      annualPrice: '¥980,000/年〜',
      licenses: '無制限',
      products: '無制限',
      apiCalls: '無制限',
      oem: true,
      features: ['OEMフル機能', 'SLA 99.9%', '専用サポート'],
      recommended: false,
    },
  ];

  const codeExample = `// 3行でライセンス検証
const result = await authena.validate("ABCD-1234-EFGH-5678");
if (result.valid) startApp();`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="fixed" sx={{ bgcolor: 'rgba(30, 60, 114, 0.95)', backdropFilter: 'blur(8px)' }}>
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0 } }}>
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 700, cursor: 'pointer' }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Authena
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                color="inherit"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              >
                料金
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<LoginIcon />}
                onClick={handleLogin}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                ログイン
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer for fixed header */}
      <Toolbar />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h6"
                sx={{ mb: 2, opacity: 0.9, fontWeight: 500 }}
              >
                日本発のライセンス管理プラットフォーム
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Authena
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                3行のコードで、ライセンス管理を完全自動化。
                <br />
                ソフトウェアの価値を守る、賢いライセンス基盤。
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    bgcolor: '#FF6B35',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 14px rgba(255, 107, 53, 0.4)',
                    '&:hover': {
                      bgcolor: '#E85A2A',
                      boxShadow: '0 6px 20px rgba(255, 107, 53, 0.5)',
                    },
                  }}
                >
                  無料で始める
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  料金プラン
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: 'rgba(0,0,0,0.3)',
                  borderRadius: 2,
                  p: 3,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <Typography
                  sx={{
                    color: '#81C784',
                    fontFamily: 'monospace',
                    mb: 1,
                  }}
                >
                  {codeExample}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h3"
          align="center"
          sx={{ mb: 2, fontWeight: 700 }}
        >
          主な機能
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          開発者のために、開発者が作ったライセンス管理基盤
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Differentiators */}
      <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 2, fontWeight: 700 }}
          >
            競合にはない強み
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            日本市場に特化した唯一のライセンス管理SaaS
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {[
              { label: '日本語UI・サポート', icon: <CheckIcon /> },
              { label: '日本円決済', icon: <CheckIcon /> },
              { label: 'マルチテナント', icon: <CheckIcon /> },
              { label: 'OEM/ホワイトラベル', icon: <CheckIcon /> },
              { label: '収益分配システム', icon: <CheckIcon /> },
              { label: '国内決済対応', icon: <CheckIcon /> },
            ].map((item, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ color: 'success.main', mb: 1 }}>
                    {item.icon}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {item.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }} id="pricing">
        <Typography
          variant="h3"
          align="center"
          sx={{ mb: 2, fontWeight: 700 }}
        >
          料金プラン
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          14日間無料トライアル・クレジットカード不要
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  border: plan.recommended ? '2px solid' : '1px solid',
                  borderColor: plan.recommended ? 'primary.main' : 'divider',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {plan.recommended && (
                  <Chip
                    label="おすすめ"
                    color="primary"
                    size="small"
                    icon={<StarIcon />}
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  />
                )}
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {plan.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h4"
                      component="span"
                      fontWeight={800}
                      color="primary.main"
                    >
                      {plan.price}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>
                  {plan.annualPrice && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      年額: {plan.annualPrice}
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>ライセンス:</strong> {plan.licenses}
                    </Typography>
                    <Typography variant="body2">
                      <strong>プロダクト:</strong> {plan.products}
                    </Typography>
                    <Typography variant="body2">
                      <strong>API:</strong> {plan.apiCalls}
                    </Typography>
                    {plan.oem && (
                      <Chip label="OEM対応" size="small" color="success" variant="outlined" />
                    )}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={0.5}>
                    {plan.features.map((feature, idx) => (
                      <Typography key={idx} variant="caption" color="text.secondary">
                        <CheckIcon sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />
                        {feature}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            align="center"
            sx={{ mb: 2, fontWeight: 700 }}
          >
            今すぐ始めましょう
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 4, opacity: 0.9 }}
          >
            14日間の無料トライアルで、すべての機能をお試しいただけます。
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#FF6B35',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(255, 107, 53, 0.4)',
                '&:hover': {
                  bgcolor: '#E85A2A',
                  boxShadow: '0 6px 20px rgba(255, 107, 53, 0.5)',
                },
              }}
            >
              無料トライアルを開始
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#0f1f3d', color: 'white', pt: 8, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Logo & Description */}
            <Grid item xs={12} md={4}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Authena
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2, lineHeight: 1.8 }}>
                日本発のライセンス管理プラットフォーム。
                3行のコードでライセンス管理を完全自動化。
              </Typography>
            </Grid>

            {/* Product Links */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                プロダクト
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="#pricing"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                  onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
                >
                  料金プラン
                </Typography>
                <Typography
                  component="a"
                  href="/login"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  ログイン
                </Typography>
              </Stack>
            </Grid>

            {/* Company Links */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                会社情報
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="https://gambit.icu"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  Gambit Inc.
                </Typography>
                <Typography
                  component="a"
                  href="mailto:info@gambit.host"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  お問い合わせ
                </Typography>
              </Stack>
            </Grid>

            {/* Other Products */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                関連サービス
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="https://www.mireet.jp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  Mireet
                </Typography>
                <Typography
                  component="a"
                  href="https://koetasu.vercel.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  コエタス
                </Typography>
              </Stack>
            </Grid>

            {/* Legal Links */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                法的情報
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="https://gambit.icu/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  利用規約
                </Typography>
                <Typography
                  component="a"
                  href="https://gambit.icu/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  プライバシーポリシー
                </Typography>
                <Typography
                  component="a"
                  href="https://gambit.icu/legal"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ opacity: 0.7, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 1 } }}
                >
                  特定商取引法に基づく表記
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          {/* Copyright */}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 4 }} />
          <Typography variant="body2" align="center" sx={{ opacity: 0.5 }}>
            &copy; {new Date().getFullYear()} Gambit Inc. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
